import DashboardLayout from "@/components/dashboard/layout/DashboardLayout";
import { SlotCalendarView } from "@/components/slots/SlotCalendarView";
import { SlotDetail } from "@/components/slots/SlotDetail";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/use-toast";
import { slotService } from "@/services/slotService";
import { Slot } from "@/types/slot";
import { ArrowLeft, Loader2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useOrganization } from "../../App";

const SlotDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [slot, setSlot] = useState<Slot | null>(null);
  const [loading, setLoading] = useState(true);
  const { currentOrganization } = useOrganization();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentOrganization?.id && id) {
      fetchSlot();
    }
  }, [currentOrganization, id]);

  const fetchSlot = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const { data, error } = await slotService.getSlot(id);
      if (error) {
        console.error("Error fetching slot:", error);
        toast({
          title: "Error",
          description: error.message || "Failed to load slot details",
          variant: "destructive",
        });
        return;
      }

      if (!data) {
        console.error("Slot not found");
        toast({
          title: "Not Found",
          description: "The requested slot could not be found",
          variant: "destructive",
        });
        return;
      }

      setSlot(data);
    } catch (error) {
      console.error("Error fetching slot:", error);
      toast({
        title: "Error",
        description: "Failed to load slot details",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSlotUpdated = () => {
    fetchSlot();
    toast({
      title: "Success",
      description: "Slot updated successfully",
    });
  };

  return (
    <DashboardLayout
      activeItem="Slots"
      title="Slot Details"
      description="View and manage slot details"
    >
      <>
        <div className="mb-4">
          <Button
            variant="outline"
            onClick={() => navigate("/slots")}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Slots
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : !slot ? (
          <div className="text-center py-12">
            <h3 className="text-lg font-medium">Slot not found</h3>
            <p className="text-muted-foreground mt-2">
              The slot you're looking for doesn't exist or has been deleted.
            </p>
            <Button onClick={() => navigate("/slots")} className="mt-4">
              Go back to Slots
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            <Tabs defaultValue="details" className="w-full">
              <TabsList>
                <TabsTrigger value="details">Slot Details</TabsTrigger>
                <TabsTrigger value="calendar">Calendar View</TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Slot Information</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <SlotDetail slot={slot} onUpdate={handleSlotUpdated} />
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="calendar" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Calendar View</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <SlotCalendarView
                      editingSlotId={id}
                      key={`slot-calendar-${id}`}
                      onSlotSegmentsUpdated={handleSlotUpdated}
                    />
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </>
    </DashboardLayout>
  );
};

export default SlotDetailPage;
